import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * Replicates shipping-label.liquid's business-days priority chain,
 * reading from data-* attributes on the add-to-cart button.
 * Falls back to the standard 3-4 day message if nothing matches,
 * so the wrong "remaining units will be delivered..." text never shows.
 *
 * @param {HTMLElement | null | undefined} btn - The add-to-cart button element.
 * @returns {string}
 */
function getBusinessDaysMessage(btn) {
  if (!btn) return "Usually leaves our warehouse in 3–4 business days";

  if (btn.getAttribute('data-3-4-business-days') === 'true') {
    return "Usually leaves our warehouse in 3–4 business days";
  }
  if (btn.getAttribute('data-5-7-business-days') === 'true') {
    return "Usually leaves our warehouse within 5–7 business days";
  }
  if (btn.getAttribute('data-7-10-business-days') === 'true') {
    return "Usually leaves our warehouse within 7–10 business days";
  }
  if (btn.getAttribute('data-special-order-8-12-weeks') === 'true') {
    return "Special order, please allow 8–12 weeks for delivery";
  }
  if (btn.getAttribute('data-special-order-12-14-weeks') === 'true') {
    return "Special order, please allow 12–14 weeks";
  }
  if (btn.getAttribute('data-special-order-14-18-weeks') === 'true') {
    return "Special order, please allow 14–18 weeks";
  }
  if (btn.getAttribute('data-made-to-order-up-to-2-weeks') === 'true') {
    return "Special order, please allow up to 2 weeks";
  }
  if (btn.getAttribute('data-made-to-order-2-4-weeks') === 'true') {
    return "Special order, please allow 2–4 weeks";
  }
  if (btn.getAttribute('data-made-to-order-4-6-weeks') === 'true') {
    return "Special order, please allow 4–6 weeks";
  }
  if (
    btn.getAttribute('data-supplier-brand') === 'CLA supplier' ||
    btn.getAttribute('data-supplier-brand') === 'CLA'
  ) {
    return "Usually leaves our warehouse within 1–2 business days";
  }

  // Ultimate fallback — same as Liquid's final else
  return "Usually leaves our warehouse in 3–4 business days";
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (event.target?.getAttribute('data-commingsoon') == "true") {
      event.target.closest('.quick-add-modal')?.close();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Public method to trigger fly-to-cart animation.
   */
  triggerFlyToCart() {
    this.#animateFlyToCart();
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  getCookie(name) {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) return decodeURIComponent(value);
    }
    return null;
  }

  setCookie(name, value, days = 7) {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}`;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
  const { addToCartTextError } = this.refs;
  event.preventDefault();

  var selectEl = document.querySelector(".variant-option__select");
  if (selectEl) {
    var selection = selectEl?.value || null;
    if (!selection) {
      var parent = selectEl.parentElement.parentElement;
      if (!parent.querySelector(".selection-error")) {
        var error = document.createElement("p");
        error.className = "selection-error";
        error.innerHTML = 'The <b>Selection</b> field is mandatory.';
        parent.appendChild(error);
      }
      selectEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
  }

  const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
  const form = this.querySelector('form');
  const formData = new FormData(form);

  if (button?.getAttribute('data-commingsoon') == "true") {
    const producttitle = button.getAttribute('data-title');
    const productcode  = button.getAttribute('data-code');
    const productsupplier = button.getAttribute('data-supplier');
    const productimg   = button.getAttribute('data-img');
    const availdateRaw = button.getAttribute('data-availdate');

    // ── READ pickup-only directly from button attribute ──
    const isPickupOnly = button.getAttribute('data-pickup-only') === 'true';

    // ── READ sell-when-out-of-stock directly from button attribute ──
    const sellWhenOOS = button.getAttribute('data-sell-when-oos') === 'true';

    // ── Business-days lead-time message, computed client-side via
    //    getBusinessDaysMessage() using the same metafield priority chain
    //    as shipping-label.liquid (3_4_business_days, 5_7_business_days,
    //    7_10_business_days, special orders, made-to-order, CLA supplier,
    //    then the "3–4 business days" default). This never returns an
    //    empty string, so the wrong "remaining units..." fallback text
    //    can no longer appear. ──
    const businessDaysMsg = getBusinessDaysMessage(button);
    const pickupBusinessDaysMsg = businessDaysMsg
      ? businessDaysMsg.replace('leaves our warehouse', 'ready for collection')
      : '';

    console.log('DEBUG pickup-only attr:', button.getAttribute('data-pickup-only'));
    console.log('DEBUG isPickupOnly:', isPickupOnly);

    // ── FIX: Sell When OOS is OFF and product is NOT pickup-only ──
    // In this configuration Shopify's native inventory policy ('deny')
    // already clamps the quantity a customer can add — a backorder /
    // insufficient-stock / ETA scenario cannot legitimately happen here.
    // So we skip the whole comingsoon confirmation flow entirely and
    // submit the form immediately, exactly as if data-commingsoon had
    // never been set. This guarantees the "Insufficient stock..." /
    // ETA popup can never appear for this combination, matching the
    // "Should not occur" expectation.
    if (!isPickupOnly && !sellWhenOOS) {
      const addToCartComponent = this.refs.addToCartButtonContainer;
      addToCartComponent?.animateAddToCart();
      addToCartComponent?.triggerFlyToCart();

      if (this.#timeout) clearTimeout(this.#timeout);

      if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

      if (!form) throw new Error('Product form element missing');

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      });

      const fetchCfg = fetchConfig('javascript', { body: formData });

      fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            window.dispatchEvent(new CartErrorEvent(this.id, response.message));

            if (!addToCartTextError) return;
            addToCartTextError.classList.remove('hidden');

            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              const newTextNode = document.createTextNode(response.message);
              addToCartTextError.appendChild(newTextNode);
            }

            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, 10000);

            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                productId: this.dataset.productId,
              })
            );

            return;
          } else {
            const id = formData.get('id');

            if (addToCartTextError) {
              addToCartTextError.classList.add('hidden');
              addToCartTextError.removeAttribute('aria-live');
            }

            if (!id) throw new Error('Form ID is required');

            if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
              const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
              const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
              const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

              this.#setLiveRegionText(addedText);

              setTimeout(() => {
                this.#clearLiveRegionText();
              }, 5000);
            }

            this.dispatchEvent(
              new CartAddEvent({}, id.toString(), {
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                productId: this.dataset.productId,
                sections: response.sections,
              })
            );
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          cartPerformance.measureFromEvent('add:user-action', event);
          const currentCount = parseInt(this.getCookie('ydpwishlistCount'), 10) || 0;
          setTimeout(() => {
            let elements = document.querySelectorAll("span.ydp-header-launcher-count");
            if (elements.length > 0) {
              elements.forEach(function(element) {
                element.innerHTML = currentCount;
              });
            }
          }, 5000);
        });

      return;
    }

    let availdateMsg = "";

    const formatDateToCustom = (dateStr) => {
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const d = new Date(dateStr);
      return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    // Only treat the ETA date as "future" if it's actually ahead of today.
    // Dates in the past (or today) should NOT trigger the "will be available from X" text.
    const isFutureDate = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return d > today;
    };

    // ── Insufficient-stock ETA wording (same for pickup and shipping) ──
    const insufficientStockEtaMsg = (availdate) =>
      `Insufficient stock on hand to fulfill order, it will be fulfilled after ${availdate}.`;

    const mainInventory = Math.max(0, Number(this.dataset.productMainInventory) || 0);
    const quantitySelected = parseInt(
      this.refs?.quantitySelectors?.[0]?.refs?.quantityInput?.value || 1, 10
    );
    const hasSufficientStock = quantitySelected <= mainInventory;

    if (isPickupOnly && availdateRaw) {
      // Both pickup only AND has ETA date
      const hasFutureDate = isFutureDate(availdateRaw);
      const availdate = formatDateToCustom(availdateRaw);

      let etaMsg = "";
      if (!hasSufficientStock) {
        const backorderQty = quantitySelected - mainInventory;
        if (mainInventory > 0) {
          etaMsg = hasFutureDate
            ? insufficientStockEtaMsg(availdate)
            : pickupBusinessDaysMsg;
        } else {
          etaMsg = hasFutureDate
            ? insufficientStockEtaMsg(availdate)
            : pickupBusinessDaysMsg;
        }
      } else {
        // Sufficient stock right now. If the ETA date has already passed,
        // still surface the business-days message (mirrors shipping-label
        // .liquid) and keep the popup open — matches previous behaviour.
        etaMsg = hasFutureDate ? "" : pickupBusinessDaysMsg;
      }

      availdateMsg = `Please note this product is available for warehouse pickup only.${etaMsg ? '\n' + etaMsg : ''}`;

    } else if (isPickupOnly) {
      // Pickup only, no ETA
      availdateMsg = "Please note this product is available for warehouse pickup only.";

    } else {
      // Not pickup only, sellWhenOOS is guaranteed true here (the
      // !isPickupOnly && !sellWhenOOS combination already returned above)
      if (availdateRaw) {
        const hasFutureDate = isFutureDate(availdateRaw);
        const availdate = formatDateToCustom(availdateRaw);

        if (!hasSufficientStock) {
          availdateMsg = hasFutureDate
            ? insufficientStockEtaMsg(availdate)
            : businessDaysMsg;
        } else {
          // Sufficient stock right now. If the ETA date has already passed,
          // still surface the business-days message (mirrors shipping-label
          // .liquid) and keep the popup open — matches previous behaviour.
          availdateMsg = hasFutureDate ? "" : businessDaysMsg;
        }
      } else {
        availdateMsg = hasSufficientStock ? "" : "Please note this product is temporarily out of stock.";
      }
    }

    if (isPickupOnly || availdateMsg) {
      // Show confirmation popup only when pickup-only OR there's an actual message to show
      const isConfirmed = await fnPickupAvailableFancyBox(producttitle, productcode, productsupplier, productimg, availdateMsg);
      if (!isConfirmed) return;
    }
  }

    const addToCartComponent = this.refs.addToCartButtonContainer;
    addToCartComponent?.animateAddToCart();
    addToCartComponent?.triggerFlyToCart();

    if (this.#timeout) clearTimeout(this.#timeout);

    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    if (!form) throw new Error('Product form element missing');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
        const currentCount = parseInt(this.getCookie('ydpwishlistCount'), 10) || 0;
        setTimeout(() => {
          let elements = document.querySelectorAll("span.ydp-header-launcher-count");
          if (elements.length > 0) {
            elements.forEach(function(element) {
              element.innerHTML = currentCount;
            });
          }
        }, 5000);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    variantId.value = event.detail.resource.id ?? '';

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    /** @type {number | null} */
    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    /**
     * @param {number} currentTime
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

/**
 * @param {number} t
 * @param {{x: number, y: number}} p0
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {{x: number, y: number}} p3
 * @returns {{x: number, y: number}}
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

// ─── GLOBAL ADD TO CART HANDLER (Algolia / search results) ──────────────────

window.handleAddToCartClick = async function (e, hit) {
  const button = e.target.closest('.ais-hit--cart-button');
  if (!button) return;

  e.preventDefault();
  e.stopPropagation();

  // Prevent duplicate clicks while validating the price
  if (button.dataset.priceChecking === 'true') return;
  button.dataset.priceChecking = 'true';

  try {
    // Get the exact variant ID from the existing hidden form
    const formId = button.getAttribute('data-form-id');
    const form = document.getElementById(formId);
    const variantId = form?.querySelector('[name="id"]')?.value;

    if (!variantId || !hit.handle) {
      alert('Unable to verify the product price. Please try again.');
      return;
    }

    // Fetch the latest product data from Shopify
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(
      `${root}products/${encodeURIComponent(hit.handle)}.js`
    );

    if (!response.ok) {
      throw new Error(`Shopify product request failed: ${response.status}`);
    }

    const product = await response.json();

    // Find the exact variant being added
    const variant = product.variants.find(
      (v) => String(v.id) === String(variantId)
    );

    if (!variant) {
      alert('Unable to verify this product variant. Please try again.');
      return;
    }

    // Shopify variant.price is returned in cents
    if (Number(variant.price) <= 0) {
      alert('This product cannot be added to the cart because its price is $0.');
      return;
    }

    // -----------------------------------------
    // Existing coming-soon / pickup logic
    // -----------------------------------------

    const isComingSoon =
      button.getAttribute('data-commingsoon') === 'true';

    if (isComingSoon) {
      document.querySelector('.quick-add-modal')?.close();

      const producttitle = button.getAttribute('data-title');
      const productcode = button.getAttribute('data-code');
      const productsupplier = button.getAttribute('data-supplier');
      const productimg = button.getAttribute('data-img');
      const availdateRaw = button.getAttribute('data-availdate');
      const isPickupOnly =
        button.getAttribute('data-pickup-only') === 'true';
      const sellWhenOOS =
        button.getAttribute('data-sell-when-oos') === 'true';

      console.log(
        'DEBUG Algolia pickup-only attr:',
        button.getAttribute('data-pickup-only')
      );
      console.log('DEBUG Algolia isPickupOnly:', isPickupOnly);

      // Preserve existing inventory behaviour
      if (!isPickupOnly && !sellWhenOOS) {
        form.submit();
        return;
      }

      let availdateMsg = '';

      const formatDateToCustom = (dateStr) => {
        const months = [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        const d = new Date(dateStr);

        return `${String(d.getDate()).padStart(2, '0')} ${
          months[d.getMonth()]
        } ${d.getFullYear()}`;
      };

      const isFutureDate = (dateStr) => {
        if (!dateStr) return false;

        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);

        return d > today;
      };

      if (isPickupOnly) {
        availdateMsg =
          'Please note this product is available for warehouse pickup only.';
      } else if (availdateRaw && isFutureDate(availdateRaw)) {
        availdateMsg =
          `Please note this product is temporarily out of stock and will be available after ${formatDateToCustom(availdateRaw)}.`;
      } else {
        availdateMsg =
          'Please note this product is temporarily out of stock.';
      }

      const isConfirmed = await fnPickupAvailableFancyBox(
        producttitle,
        productcode,
        productsupplier,
        productimg,
        availdateMsg
      );

      if (!isConfirmed) return;
    }

    // Submit only after Shopify confirms the variant has a price > $0
    form.submit();

  } catch (error) {
    console.error('Shopify price validation failed:', error);
    alert('Unable to verify the product price. Please try again.');
  } finally {
    button.dataset.priceChecking = 'false';
  }
};


async function fnPickupAvailableFancyBox(producttitle, productcode, productsupplier, productimg, availdateMsg) {
  return await new Promise((resolve) => {
    $.fancybox([
      {
        content: `
          <div id="product-enquire-popup" class="confirm-backorder-popup">
            <style>.confirm-backorder-popup{min-width:700px;}@media(max-width:991px){.confirm-backorder-popup{min-width:1px;}}</style>
            <div class="grey-box-details">
              <div class="grey-box-title">Product</div>
              <div class="grey-box-inner">
                <div class="grey-product-content">
                  <div class="grey-box-product">${producttitle}</div>
                  ${productcode    ? `<div class="grey-box-productcode">Code: ${productcode}</div>`         : ''}
                  ${productsupplier ? `<div class="grey-box-productsupplier">Supplier: ${productsupplier}</div>` : ''}
                </div>
                <img style="max-height:150px" src="${productimg}" class="grey-box-product-image">
              </div>
              <div class="clearfix"></div>
            </div>
          </div>
          <div class="prod-coming-soon">
            ${availdateMsg.replace(/\.\s+/g, '.<br>')}
          </div>
          <div class="cart-buttons">
            <input type="submit" class="checkout-btn confirm-btn" id="confirmBack" value="Confirm">
            <div class="float-right">
              <input type="button" class="continue-btn" id="cancelBack" value="Cancel">
            </div>
          </div>`,
        type: 'html'
      }
    ], {
      width: 800,
      height: 575,
      afterShow: function() {
        $('#confirmBack').on('click', function() { $.fancybox.close(); resolve(true); });
        $('#cancelBack').on('click',  function() { $.fancybox.close(); resolve(false); });
      }
    });
  });
}